import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import useUserStore from '../../store/userStore';
import {createSchedule, getScheduleList, deleteSchedule, ScheduleResponse, ChainedStep} from '../../api/schedulerApi';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Image from '@tiptap/extension-image';
import axios from 'axios';
import { baseURL } from '../../lib/apiClient';
import MemoManager from "./MemoManager";

// --- 스타일 ---
const ListContentPreview = styled.div`
  font-size: 0.85rem;
  color: var(--text-bright);
  margin-bottom: 4px;

  /* ✨ [수정] 모든 이미지를 80x60 사이즈로 통일 */
  img {
    width: 80px;
    height: 60px;
    object-fit: cover; /* 비율 유지하며 꽉 채움 */
    border-radius: 4px;
    margin-top: 4px;
    cursor: pointer; /* 클릭 가능 표시 */
    border: 1px solid var(--border-color);
    transition: transform 0.2s;
    &:hover { transform: scale(1.05); }
  }
  p { margin: 0; }
`;

// ✨ [신규] 원본 이미지 전용 모달 스타일
const ImageZoomModal = styled.div`
  position: fixed; top: 0; left: 0; right: 0; bottom: 0;
  background-color: rgba(0,0,0,0.85);
  z-index: 11000; /* 알림 목록 모달(10000)보다 높게 설정 */
  display: flex; justify-content: center; align-items: center;
  cursor: zoom-out;

  /* 배경 클릭 시 하위 모달에 영향을 주지 않도록 설정 */
  pointer-events: auto;
`;

const FabContainer = styled.div`
  position: fixed; bottom: 20px; left: 20px; z-index: 9995;
`;

const HamburgerButton = styled.button`
  width: 48px; height: 48px; border-radius: 50%;
  background-color: var(--bg-card); border: 1px solid var(--border-color);
  box-shadow: 0 4px 6px rgba(0,0,0,0.1); cursor: pointer;
  display: flex; align-items: center; justify-content: center;
  transition: transform 0.2s;
  &:hover { transform: scale(1.1); background-color: var(--bg-input); }
  svg { width: 24px; height: 24px; fill: var(--text-bright); }
`;

const MenuPopup = styled.div`
  position: absolute; bottom: 60px; left: 0;
  width: 160px; background-color: var(--bg-card);
  border: 1px solid var(--border-color); border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0,0,0,0.15); overflow: hidden;
  display: flex; flex-direction: column;
`;

const MenuItem = styled.button`
  padding: 12px; text-align: left; background: none; border: none;
  color: var(--text-bright); cursor: pointer; font-size: 0.9rem;
  &:hover { background-color: var(--bg-input); color: var(--primary-accent); }
  border-bottom: 1px solid var(--border-color);
  &:last-child { border-bottom: none; }
`;

const ModalOverlay = styled.div`
  position: fixed; top: 0; left: 0; right: 0; bottom: 0;
  background-color: rgba(0,0,0,0.5); z-index: 10000;
  display: flex; justify-content: center; align-items: center;
`;

const ModalTitle = styled.h3`margin: 0; font-size: 1.2rem;`;
const InputGroup = styled.div`display: flex; flex-direction: column; gap: 5px;`;
const Label = styled.label`font-size: 0.85rem; color: var(--text-normal);`;
const Input = styled.input`
  padding: 8px; border-radius: 4px; border: 1px solid var(--border-color);
  background: var(--bg-input); color: var(--text-bright);
`;
const Select = styled.select`
  padding: 8px; border-radius: 4px; border: 1px solid var(--border-color);
  background: var(--bg-input); color: var(--text-bright);
`;
const ButtonGroup = styled.div`display: flex; justify-content: flex-end; gap: 10px; margin-top: 10px;`;
const ActionButton = styled.button<{ primary?: boolean }>`
  padding: 8px 16px; border-radius: 4px; border: none; cursor: pointer;
  background-color: ${props => props.primary ? 'var(--primary-accent)' : 'var(--bg-input)'};
  color: ${props => props.primary ? 'white' : 'var(--text-bright)'};
  &:hover { opacity: 0.9; }
`;

const ListContainer = styled.div`max-height: 300px; overflow-y: auto;`;
const ListItem = styled.div`
  padding: 10px; border-bottom: 1px solid var(--border-color);
  display: flex; justify-content: space-between; align-items: center;
  &:last-child { border-bottom: none; }
`;
const ItemInfo = styled.div`display: flex; flex-direction: column; gap: 2px;`;
const SmallText = styled.span`font-size: 0.75rem; color: var(--text-dark);`;
const MiddleText = styled.span`font-size: 1.05rem; color: var(--text-dark);`;

const ModalBox = styled.div`
  background: var(--bg-card);
  width: 650px;
  max-width: 95vw;
  max-height: 90vh;
  padding: 24px;
  border-radius: 12px;
  border: 1px solid var(--border-color);
  color: var(--text-bright);
  display: flex;
  flex-direction: column;
  gap: 15px;
  overflow-y: auto;

  @media (max-width: 768px) {
    width: 95vw;
    /* ✨ [수정] 모바일 브라우저 UI 가림 방지: 100vh 대신 85dvh 사용 및 여백 확보 */
    height: auto;
    max-height: 80dvh;
    border-radius: 8px;
    padding: 16px;
    gap: 12px;

    /* ✨ 모바일에서 입력 행들을 세로로 배치하여 공간 절약 */
    & > div[style*="display: flex"] {
      flex-direction: column !important;
      align-items: stretch !important;
      gap: 8px !important;
    }

    /* 하단 버튼 그룹은 가로 정렬 유지 */
    div:last-child {
      flex-direction: row !important;
      justify-content: flex-end !important;
      gap: 10px !important;
    }

    /* 라디오 버튼 등 인라인 요소들은 가로 흐름 유지 */
    label {
      width: auto !important;
    }
  }
`;

const EditorContainer = styled.div`
  border: 1px solid var(--border-color);
  border-radius: 4px;
  background: var(--bg-input);

  .ProseMirror {
    min-height: 200px; max-height: 400px; padding: 12px; outline: none;
    color: var(--text-bright); overflow-y: auto;
    strong { font-weight: bold !important; }
    ul { list-style-type: disc; padding-left: 1.5rem; margin: 0.5rem 0; }
    ol { list-style-type: decimal; padding-left: 1.5rem; margin: 0.5rem 0; }
    li { margin-bottom: 0.25rem; }
    img { max-width: 100%; max-height: 300px; object-fit: contain; border-radius: 4px; display: block; margin: 10px 0; }
  }
`;

const Toolbar = styled.div`
  padding: 8px; border-bottom: 1px solid var(--border-color); background: #f1f1f1; display: flex; gap: 6px; flex-wrap: wrap;
`;

const ToolbarButton = styled.button<{ active?: boolean }>`
  padding: 4px 10px; font-size: 0.85rem; font-weight: bold; cursor: pointer;
  background: ${props => props.active ? '#ccc' : '#fff'};
  border: 1px solid #bbb; border-radius: 4px; color: #333;
  &:hover { background: #eee; }
`;

type RecurringType = 'INTERVAL' | 'DAILY' | 'WEEKLY' | 'MONTHLY' | 'YEARLY';

const SchedulerManager = () => {
    const { user } = useUserStore();
    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const [modalMode, setModalMode] = useState<'NONE' | 'CREATE' | 'LIST'>('NONE');

    const [targetId, setTargetId] = useState('');
    const [isTargetEditable, setIsTargetEditable] = useState(false);

    const [scheduleType, setScheduleType] = useState<'ONCE' | 'RECURRING'>('ONCE');
    const [onceMode, setOnceMode] = useState<'NOW' | 'DELAY' | 'PICKER'>('NOW');
    const [delayMinutes, setDelayMinutes] = useState(1);
    const [selectedDateTime, setSelectedDateTime] = useState('');

    const [recurringType, setRecurringType] = useState<RecurringType>('INTERVAL');
    const [intervalMinutes, setIntervalMinutes] = useState(1);
    const [selectedTime, setSelectedTime] = useState('09:00');
    const [selectedDayOfWeek, setSelectedDayOfWeek] = useState('MON');
    const [selectedDayOfMonth, setSelectedDayOfMonth] = useState(1);
    const [selectedMonth, setSelectedMonth] = useState(1);

    const [scheduleList, setScheduleList] = useState<ScheduleResponse[]>([]);

    const [notificationTool, setNotificationTool] = useState<'BROWSER' | 'DISCORD' | 'DEADLINE_WORKFLOW'>('BROWSER'); // ✨ 워크플로우 추가
    const [days, setDays] = useState(1); // 마감 임박 기준일
    const [apiId, setApiId] = useState<string>('2'); // 디스코드용 apiId

    const [priority, setPriority] = useState<'URGENT' | 'NORMAL'>('NORMAL');
    const [selectedImgUrl, setSelectedImgUrl] = useState<string | null>(null); // ✨ 원본 이미지 URL 상태

    const [editingJobName, setEditingJobName] = useState<string | null>(null); // ✨ 수정 시 기존 이름 보관용

    const [isMemoOpen, setIsMemoOpen] = useState(false);
    const [jobDescription, setJobDescription] = useState('');

    // ✨ [신규] 기존 데이터를 UI 상태로 복구하는 함수
    const populateFormFromJob = (job: ScheduleResponse) => {
        const actionType = job.actionType || job.jobData?.actionType;
        const isChained = actionType === 'CHAINED_REST_API';

        setJobDescription(job.description || "");

        // 1. 알림 도구 및 내용 파싱
        let contentHtml = "";

        if (isChained && job.jobData?.executionPlan) {
            setNotificationTool('DEADLINE_WORKFLOW');
            try {
                // executionPlan 파싱
                const plan = typeof job.jobData.executionPlan === 'string'
                    ? JSON.parse(job.jobData.executionPlan)
                    : job.jobData.executionPlan;

                // 첫 번째 단계에서 마감 기준일(days) 추출
                const step1 = plan.find((s: any) => s.order === 1);
                if (step1 && step1.staticQueryParams) {
                    const qParams = typeof step1.staticQueryParams === 'string'
                        ? JSON.parse(step1.staticQueryParams)
                        : step1.staticQueryParams;
                    setDays(qParams.days || 1);
                }
            } catch (e) {
                console.error("Workflow 데이터 복구 실패:", e);
            }
        } else {
            try {
                if (job.jobData.message) {
                    // 브라우저 알림 복구
                    const parsed = JSON.parse(job.jobData.message);
                    contentHtml = parsed.msg || "";
                    setNotificationTool('BROWSER');
                } else if (job.jobData.bodyParams) {
                    // 디스코드 알림 복구
                    const parsed = JSON.parse(job.jobData.bodyParams);
                    setNotificationTool('DISCORD');
                    setApiId(String(job.jobData.apiId || '2'));
                    setPriority(parsed.embeds?.[0]?.color === 16711680 ? 'URGENT' : 'NORMAL');

                    // 💡 백업된 rawHtml이 있으면 사용, 없으면 description과 이미지 조합으로 복구
                    if (parsed.rawHtml) {
                        contentHtml = parsed.rawHtml;
                    } else {
                        const desc = parsed.embeds?.[0]?.description || "";
                        contentHtml = desc.includes("**내용:**\n") ? desc.split("**내용:**\n")[1] : desc;
                        const imgUrl = parsed.embeds?.[0]?.image?.url;
                        if (imgUrl) contentHtml += `<p><img src="${imgUrl}" /></p>`;
                    }
                }
            } catch (e) { console.error(e); }
        }

        editor?.commands.setContent(contentHtml);
        setTargetId(job.jobData.targetUserId || user?.id || "");

        // 2. 예약 방식 및 시간 복구
        setScheduleType(job.jobType as 'ONCE' | 'RECURRING');
        if (job.jobType === 'ONCE' && job.nextFireTime) {
            setOnceMode('PICKER');
            setSelectedDateTime(job.nextFireTime.slice(0, 16)); // YYYY-MM-DDTHH:mm 형식
        } else if (job.jobType === 'RECURRING' && job.cronExpression) {
// 2. 반복성 예약(Cron) 복구
            const parts = job.cronExpression.split(' ');
            const minute = parts[1];
            const hour = parts[2];
            const dayOfMonth = parts[3];
            const month = parts[4];
            const dayOfWeek = parts[5];

            // 2-1. 간격 반복 (예: 0 0/5 * * * ?)
            if (minute.includes('/')) {
                const interval = minute.split('/')[1];
                setRecurringType('INTERVAL');
                setIntervalMinutes(Number(interval));
            }
            // 2-2. 매년 반복 (예: 0 30 14 15 12 ?)
            else if (month !== '*' && month !== '?') {
                setRecurringType('YEARLY');
                setSelectedMonth(Number(month));
                setSelectedDayOfMonth(Number(dayOfMonth));
                setSelectedTime(`${hour.padStart(2, '0')}:${minute.padStart(2, '0')}`);
            }
            // 2-3. 매월 반복 (예: 0 30 14 15 * ?)
            else if (dayOfMonth !== '*' && dayOfMonth !== '?') {
                setRecurringType('MONTHLY');
                setSelectedDayOfMonth(Number(dayOfMonth));
                setSelectedTime(`${hour.padStart(2, '0')}:${minute.padStart(2, '0')}`);
            }
            // 2-4. 매주 반복 (예: 0 30 14 ? * MON)
            else if (dayOfWeek !== '*' && dayOfWeek !== '?') {
                setRecurringType('WEEKLY');
                setSelectedDayOfWeek(dayOfWeek);
                setSelectedTime(`${hour.padStart(2, '0')}:${minute.padStart(2, '0')}`);
            }
            // 2-5. 매일 반복 (예: 0 30 14 * * ?)
            else {
                setRecurringType('DAILY');
                setSelectedTime(`${hour.padStart(2, '0')}:${minute.padStart(2, '0')}`);
            }
        }
    };

    // ✨ 수정 모달 열기
    const openEditModal = (job: ScheduleResponse) => {
        setIsMenuOpen(false);
        setEditingJobName(job.jobName); // 기존 이름 유지 (Overwrite 발생)
        populateFormFromJob(job);
        setModalMode('CREATE');
    };

    // ✨ 복사 모달 열기
    const openCopyModal = (job: ScheduleResponse) => {
        setIsMenuOpen(false);
        setEditingJobName(null); // 이름 비움 (신규 생성)
        populateFormFromJob(job);
        setModalMode('CREATE');
    };

    // ✨ [신규] ESC 키를 통한 단계별 모달 닫기 로직
    useEffect(() => {
        const handleKeyDown = (e: KeyboardEvent) => {
            if (e.key === 'Escape') {
                if (selectedImgUrl) {
                    // 1순위: 이미지 확대 모달이 있으면 그것부터 닫음
                    setSelectedImgUrl(null);
                } else if (modalMode !== 'NONE') {
                    // 2순위: 이미지 모달이 없으면 일반 모달(생성/목록)을 닫음
                    setModalMode('NONE');
                }
            }
        };

        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [selectedImgUrl, modalMode]); // 상태 변화 시 리스너 업데이트

    // ✨ [신규] 이미지 클릭 핸들러 (이벤트 위임 활용)
    const handleImageClick = (e: React.MouseEvent) => {
        const target = e.target as HTMLElement;
        if (target.tagName === 'IMG') {
            setSelectedImgUrl((target as HTMLImageElement).src);
        }
    };

    // 요일 영문-한글 매핑
    const DAY_MAP: Record<string, string> = {
        'MON': '월요일', 'TUE': '화요일', 'WED': '수요일',
        'THU': '목요일', 'FRI': '금요일', 'SAT': '토요일', 'SUN': '일요일'
    };

    /**
     * Cron 식을 사람이 읽기 편한 한글로 변환합니다.
     * @param cron "0 0 9 ? * MON" 형태의 Quartz Cron 식
     */
    const parseCronToHumanReadable = (cron: string): string => {
        if (!cron) return "알 수 없는 주기";

        const parts = cron.split(' ');
        // parts 순서: 0(초), 1(분), 2(시), 3(일), 4(월), 5(요일)

        // 1. 간격 반복 처리 (예: 0 0/5 * * * ?)
        if (parts[1].includes('/')) {
            const interval = parts[1].split('/')[1];
            return `매 ${interval}분마다`;
        }

        const hour = parts[2].padStart(2, '0');
        const minute = parts[1].padStart(2, '0');

        // 2. 매년 반복 처리 (예: 0 0 9 1 12 ?)
        if (parts[4] !== '*' && parts[4] !== '?') {
            return `매년 ${parts[4]}월 ${parts[3]}일 ${hour}:${minute}`;
        }

        // 3. 매월 반복 처리 (예: 0 0 9 15 * ?)
        if (parts[3] !== '*' && parts[3] !== '?') {
            return `매월 ${parts[3]}일 ${hour}:${minute}`;
        }

        // 4. 매주 반복 처리 (예: 0 0 9 ? * MON)
        if (parts[5] !== '*' && parts[5] !== '?') {
            const day = DAY_MAP[parts[5]] || parts[5];
            return `매주 ${day} ${hour}:${minute}`;
        }

        // 5. 매일 반복 처리 (예: 0 0 9 * * ?)
        return `매일 ${hour}:${minute}`;
    };

    const editor = useEditor({
        extensions: [StarterKit, Image],
        content: '',
    });

    const getInitialDateTime = () => {
        const now = new Date();
        now.setMinutes(now.getMinutes() + 1);
        now.setSeconds(0);
        return now.toISOString().slice(0, 16);
    };

    useEffect(() => {
        if (user) setTargetId(user.id);
        setSelectedDateTime(getInitialDateTime());
    }, [user]);

    const handleImageUpload = () => {
        const input = document.createElement('input');
        input.type = 'file';
        input.accept = 'image/*';
        input.onchange = async () => {
            const file = input.files?.[0];
            if (!file) return;

            const formData = new FormData();
            formData.append('file', file);

            try {
                const response = await axios.post(`${baseURL}/upload`, formData, {
                    headers: { 'Content-Type': 'multipart/form-data' },
                    withCredentials: true
                });
                const responseText = response.data;
                const fileName = responseText.includes(': ') ? responseText.split(': ')[1] : null;

                if (fileName) {
                    const imageUrl = `${baseURL}/download/${fileName}`;
                    editor?.chain().focus().setImage({ src: imageUrl }).run();
                }
            } catch (error) {
                alert("이미지 업로드에 실패했습니다.");
            }
        };
        input.click();
    };

    const getFireTime = () => {
        if (onceMode === 'NOW') return undefined;
        if (onceMode === 'PICKER') {
            return selectedDateTime.replace('T', ' ') + ":00";
        } else {
            const now = new Date();
            now.setMinutes(now.getMinutes() + delayMinutes);
            return now.getFullYear() + "-" +
                String(now.getMonth() + 1).padStart(2, '0') + "-" +
                String(now.getDate()).padStart(2, '0') + " " +
                String(now.getHours()).padStart(2, '0') + ":" +
                String(now.getMinutes()).padStart(2, '0') + ":" +
                String(now.getSeconds()).padStart(2, '0');
        }
    };

    const generateCron = () => {
        if (recurringType === 'INTERVAL') {
            return `0 0/${intervalMinutes} * * * ?`;
        }
        const [hour, minute] = selectedTime.split(':');
        switch (recurringType) {
            case 'DAILY':   return `0 ${minute} ${hour} * * ?`;
            case 'WEEKLY':  return `0 ${minute} ${hour} ? * ${selectedDayOfWeek}`;
            case 'MONTHLY': return `0 ${minute} ${hour} ${selectedDayOfMonth} * ?`;
            case 'YEARLY':  return `0 ${minute} ${hour} ${selectedDayOfMonth} ${selectedMonth} ?`;
            default: return "";
        }
    };

    const handleCreate = async () => {
        const isChained = notificationTool === 'DEADLINE_WORKFLOW';
        let chainedSteps: ChainedStep[] | undefined = undefined;

        if (isChained) {
            chainedSteps = [
                {
                    order: 1,
                    apiId: 3, // 마감 체크 API
                    authType: 'SYSTEM',
                    staticQueryParams: JSON.stringify({ days: days })
                },
                {
                    order: 2,
                    apiId: 2, // 디스코드 발송 API
                    authType: 'NONE',
                    staticBodyParams: JSON.stringify({ username: "업무 마감 봇" }),
                    bodyParamMapping: {
                        // 개행 처리가 적용된 새 템플릿
                        content: "🔔 **마감 임박 리스트** 🔔\n마감 " + days + "일 이내 업무들\n{{ prev.response | • {{title}} (기한: {{dueDate}}) }}"
                    },
                    runCondition: {
                        target: "{{prev.response}}",
                        operator: "NOT_EMPTY"
                    }
                }
            ];
        }

        const htmlContent = editor?.getHTML() || '';
        // 1. 유효성 검사 분기 처리
        if (!isChained) {
            // 일반 알림(브라우저, 디스코드)일 때만 에디터 내용 체크
            const htmlContent = editor?.getHTML() || '';
            if (htmlContent === '<p></p>' || htmlContent === '') {
                return alert("내용을 입력하세요.");
            }
        } else {
            // 워크플로우 알림일 때는 에디터 대신 필수 설정값 확인
            if (!days || days < 1) {
                return alert("마감 임박 기준일을 1일 이상으로 설정해주세요.");
            }
        }
        if (notificationTool === 'DISCORD' && !apiId) return alert("API ID를 입력하세요.");

        // 브라우저 알림용 메시지 규격
        const browserMessage = JSON.stringify({
            targetUserId: targetId,
            msg: htmlContent,
            isHtml: true,
            createdAt: new Date().toISOString()
        });

        const isDiscord = notificationTool === 'DISCORD';

        // ✨ [신규] 이미지 URL 추출 로직
        // 에디터 HTML에서 첫 번째 <img> 태그의 src 속성 값을 찾아냅니다.
        const imgRegex = /<img [^>]*src="([^"]+)"[^>]*>/;
        const match = htmlContent.match(imgRegex);
        const imageUrl = match ? match[1] : null;
        const plainText = htmlContent.replace(/<[^>]*>?/gm, ''); // 태그 제거

        // 긴급도 설정
        const discordColor = priority === 'URGENT' ? 16711680 : 65280;
        const priorityEmoji = priority === 'URGENT' ? '🚨 [긴급]' : '✅ [일반]';

        // ✨ [수정] Discord Embed 구조에 이미지 추가
        const embed: any = {
            title: priority === 'URGENT' ? "⚠️ 긴급 알림 확인 요망" : "알림 정보",
            description: `**보낸 사람:** ${user?.id}\n**받는 사람:** ${targetId}\n\n**내용:**\n${plainText}`,
            color: discordColor
        };

        // 추출된 이미지 URL이 있다면 embed에 추가합니다.
        if (imageUrl) {
            embed.image = { url: imageUrl };
        }

        // 2. ✨ Discord용 Params 구조 생성
        const discordParams = JSON.stringify({
            username: "긴급상황 고양이",
            content: `${priorityEmoji} 새로운 알림이 도착했습니다!`,
            embeds: [embed],
            rawHtml: htmlContent
        });

        // Picker 모드일 때 날짜 선택 확인
        if (scheduleType === 'ONCE' && onceMode === 'PICKER' && !selectedDateTime) {
            return alert("날짜와 시간을 선택해주세요.");
        }

        try {
            await createSchedule({
                jobName: editingJobName || `ALARM_${user?.id}_${Date.now()}`,
                jobGroup: 'USER_ALARM_GROUP',
                jobType: scheduleType,
                fireTime: scheduleType === 'ONCE' ? getFireTime() : undefined,
                cronExpression: scheduleType === 'RECURRING' ? generateCron() : undefined,
                callerId: 'WORK_MANAGER',
                callerUserId: user?.id ? user?.id : 'anonymous',
                actionType: isChained ? 'CHAINED_REST_API' : (notificationTool === 'DISCORD' ? 'REST_API' : 'MESSAGE_BROKER'),
                chainedSteps: chainedSteps, // ✨ 워크플로우 데이터 전달
                apiId: isDiscord ? Number(apiId) : undefined,
                customBodyParams: isDiscord ? discordParams : undefined,
                customQueryParams: undefined, // 필요 시 쿼리 파라미터 추가
                messageBody: isDiscord ? undefined : browserMessage,
                destination: isDiscord ? undefined : 'alarm-stream1',
                description: jobDescription,
            });
            alert(editingJobName ? "수정되었습니다!" : "생성되었습니다!");
            setModalMode('NONE');
            resetForm();
            setEditingJobName(null);
        } catch (e) { console.error(e); }
    };

    const resetForm = () => {
        if (editor) editor.commands.clearContent();
        if (user) setTargetId(user.id);
        setIsTargetEditable(false);
        setScheduleType('ONCE');
        setOnceMode('DELAY');
        setNotificationTool('BROWSER');
        setRecurringType('INTERVAL');
        setApiId('2');
        setPriority('NORMAL');
        setSelectedDateTime(getInitialDateTime());
        setEditingJobName(null);
        setJobDescription('');
    };

    const toggleMenu = () => setIsMenuOpen(!isMenuOpen);

    const openCreateModal = () => {
        resetForm();
        setIsMenuOpen(false);
        setModalMode('CREATE');
        if (user) setTargetId(user.id);
        setIsTargetEditable(false);
    };

    const openListModal = async () => {
        setIsMenuOpen(false);
        setModalMode('LIST');
        await fetchList();
    };

    const fetchList = async () => {
        if (!user) return;
        try {
            const res = await getScheduleList();
            setScheduleList(res.data);
        } catch (e) {
            alert("목록 불러오기 실패");
        }
    };

    const handleDelete = async (jobName: string, jobGroup: string) => {
        if (!window.confirm("정말 삭제하시겠습니까?")) return;
        try {
            await deleteSchedule(jobName, jobGroup);
            await fetchList();
        } catch (e) {
            alert("삭제 실패");
        }
    };

    if (!user) return null;

    return (
        <>
            <FabContainer>
                {isMenuOpen && (
                    <MenuPopup>
                        <MenuItem onClick={() => { setIsMenuOpen(false); setIsMemoOpen(true); }}>
                            📝 메모 작성
                        </MenuItem>
                        <MenuItem onClick={openCreateModal}>⏰ 알림 설정</MenuItem>
                        <MenuItem onClick={openListModal}>📋 알림 목록</MenuItem>
                    </MenuPopup>
                )}
                <HamburgerButton onClick={toggleMenu}>
                    <svg viewBox="0 0 24 24"><path d="M3 18h18v-2H3v2zm0-5h18v-2H3v2zm0-7v2h18V6H3z"/></svg>
                </HamburgerButton>
            </FabContainer>

            <MemoManager isOpen={isMemoOpen} onClose={() => setIsMemoOpen(false)} />

            {modalMode === 'CREATE' && (
                <ModalOverlay onClick={() => {setModalMode('NONE'); resetForm();}}>
                    <ModalBox onClick={e => e.stopPropagation()}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                            <ModalTitle>
                                {editingJobName ? '🔔 알림 수정하기' : '✨ 새 알림 예약'}
                            </ModalTitle>
                            <ActionButton
                                onClick={() => {
                                    if(window.confirm("입력한 모든 내용을 초기화할까요?")) {
                                        resetForm();
                                    }
                                }}
                                style={{ fontSize: '0.8rem', padding: '4px 10px', background: 'var(--bg-input)' }}
                            >
                                🔄 양식 초기화
                            </ActionButton>
                        </div>

                        {/* 1. 알림 도구 및 기본 설정 (한 줄 배치) */}
                        <div style={{ display: 'flex', gap: '15px', alignItems: 'flex-end' }}>
                            <InputGroup style={{ flex: 1 }}>
                                <Label>작업 메모/설명</Label>
                                <Input
                                    value={jobDescription}
                                    onChange={e => setJobDescription(e.target.value)}
                                    placeholder="예: 매일 오전 마감 업무 리스트 발송"
                                />
                            </InputGroup>

                            <InputGroup style={{ flex: 2 }}>
                                <Label>알림 도구 선택</Label>
                                <Select value={notificationTool} onChange={e => setNotificationTool(e.target.value as any)}>
                                    <option value="BROWSER">🌐 브라우저 알림</option>
                                    <option value="DISCORD">💬 디스코드 웹훅 (DB 등록형)</option>
                                    <option value="DEADLINE_WORKFLOW">🎯 [워크플로우] 업무 마감 자동 알림</option>
                                </Select>
                            </InputGroup>

                            {/* 디스코드일 때만 API ID 입력칸 표시 */}
                            {notificationTool === 'DISCORD' && (
                                <InputGroup style={{ flex: 1 }}>
                                    <Label>API ID (DB 번호)</Label>
                                    <Input
                                        type="number"
                                        value={apiId}
                                        onChange={e => setApiId(e.target.value)}
                                    />
                                </InputGroup>
                            )}
                        </div>

                        {/* 2. 알림 도구별 상세 설정 (상황별 노출) */}
                        {notificationTool === 'DEADLINE_WORKFLOW' ? (
                            /* ✨ 워크플로우 전용 설정 박스 */
                            <div style={{
                                background: 'rgba(var(--primary-accent-rgb), 0.1)',
                                padding: '15px',
                                borderRadius: '8px',
                                border: '1px solid var(--primary-accent)',
                                marginTop: '5px'
                            }}>
                                <InputGroup>
                                    <Label style={{ fontWeight: 'bold', color: 'var(--primary-accent)' }}>📅 마감 임박 기준 설정</Label>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginTop: '5px' }}>
                                        <Input
                                            type="number"
                                            value={days}
                                            onChange={e => setDays(Number(e.target.value))}
                                            min={1}
                                            style={{ width: '80px', textAlign: 'center' }}
                                        />
                                        <span style={{ fontSize: '0.9rem' }}>일 이내의 업무를 수집하여 디스코드로 자동 발송합니다.</span>
                                    </div>
                                </InputGroup>
                            </div>
                        ) : (
                            /* ✨ 일반 알림용 에디터 */
                            <InputGroup>
                                <Label>알림 상세 내용</Label>
                                <EditorContainer>
                                    <Toolbar>
                                        <ToolbarButton onClick={() => editor?.chain().focus().toggleBold().run()} active={editor?.isActive('bold')}>B</ToolbarButton>
                                        <ToolbarButton onClick={() => editor?.chain().focus().toggleBulletList().run()} active={editor?.isActive('bulletList')}>• List</ToolbarButton>
                                        <ToolbarButton onClick={() => editor?.chain().focus().toggleOrderedList().run()} active={editor?.isActive('orderedList')}>1. List</ToolbarButton>
                                        {notificationTool === 'BROWSER' && (
                                            <ToolbarButton onClick={handleImageUpload}>🖼 이미지 업로드</ToolbarButton>
                                        )}
                                    </Toolbar>
                                    <EditorContent editor={editor} />
                                </EditorContainer>
                            </InputGroup>
                        )}

                        {/* 3. 공통 설정 (대상자, 예약 방식) */}
                        <div style={{ borderTop: '1px solid var(--border-color)', paddingTop: '15px', marginTop: '5px', display: 'flex', flexDirection: 'column', gap: '15px' }}>
                            <InputGroup>
                                <Label>대상 사용자 (ID)</Label>
                                <div style={{ display: 'flex', gap: '8px' }}>
                                    <Input value={targetId} onChange={e => setTargetId(e.target.value)} disabled={!isTargetEditable} style={{ flex: 1 }} />
                                    <ActionButton onClick={() => setIsTargetEditable(!isTargetEditable)} type="button" style={{ fontSize: '0.8rem' }}>
                                        {isTargetEditable ? '내 계정으로' : '다른 사용자'}
                                    </ActionButton>
                                </div>
                            </InputGroup>

                            <div style={{ display: 'flex', gap: '20px', alignItems: 'center' }}>
                                <InputGroup>
                                    <Label>예약 방식</Label>
                                    <div style={{ display: 'flex', gap: '15px', marginTop: '5px' }}>
                                        <label style={{ display: 'flex', alignItems: 'center', gap: '5px', cursor: 'pointer' }}>
                                            <input type="radio" checked={scheduleType === 'ONCE'} onChange={() => setScheduleType('ONCE')} /> 단발성
                                        </label>
                                        <label style={{ display: 'flex', alignItems: 'center', gap: '5px', cursor: 'pointer' }}>
                                            <input type="radio" checked={scheduleType === 'RECURRING'} onChange={() => setScheduleType('RECURRING')} /> 반복성
                                        </label>
                                    </div>
                                </InputGroup>

                                {/* 긴급도 설정 (디스코드일 때만 활성화 가능하도록 구성 가능) */}
                                {notificationTool === 'DISCORD' && (
                                    <InputGroup>
                                        <Label>중요도</Label>
                                        <div style={{ display: 'flex', gap: '15px', marginTop: '5px' }}>
                                            <label style={{ display: 'flex', alignItems: 'center', gap: '5px', cursor: 'pointer' }}>
                                                <input type="radio" checked={priority === 'NORMAL'} onChange={() => setPriority('NORMAL')} /> ✅ 일반
                                            </label>
                                            <label style={{ display: 'flex', alignItems: 'center', gap: '5px', cursor: 'pointer' }}>
                                                <input type="radio" checked={priority === 'URGENT'} onChange={() => setPriority('URGENT')} /> 🚨 긴급
                                            </label>
                                        </div>
                                    </InputGroup>
                                )}
                            </div>
                        </div>

                        {/* 4. 시간/주기 설정 영역 */}
                        <div style={{ background: 'var(--bg-input)', padding: '15px', borderRadius: '8px' }}>
                            {scheduleType === 'ONCE' && (
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                                    <div style={{ display: 'flex', gap: '10px' }}>
                                        <ToolbarButton active={onceMode === 'NOW'} onClick={() => setOnceMode('NOW')}>🚀 즉시 발송</ToolbarButton>
                                        <ToolbarButton active={onceMode === 'DELAY'} onClick={() => setOnceMode('DELAY')}>⏱️ 지연 발송</ToolbarButton>
                                        <ToolbarButton active={onceMode === 'PICKER'} onClick={() => setOnceMode('PICKER')}>📅 날짜 지정</ToolbarButton>
                                    </div>
                                    {onceMode === 'NOW' && (
                                        <div style={{ fontSize: '0.85rem', color: 'var(--primary-accent)', padding: '5px' }}>
                                            입력한 설정으로 지금 즉시 알림이 발송됩니다.
                                        </div>
                                    )}

                                    {onceMode === 'DELAY' && (
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                            <Input type="number" min="1" value={delayMinutes} onChange={e => setDelayMinutes(Math.max(1, Number(e.target.value)))} style={{ width: '80px' }} />
                                            <span>분 뒤에 발송됩니다.</span>
                                        </div>
                                    )}

                                    {onceMode === 'PICKER' && (
                                        <Input type="datetime-local" value={selectedDateTime} onChange={e => setSelectedDateTime(e.target.value)} style={{ width: '100%' }} />
                                    )}
                                </div>
                            )}

                            {scheduleType === 'RECURRING' && (
                                <div style={{ background: 'rgba(0,0,0,0.05)', padding: '15px', borderRadius: '8px' }}>
                                    <InputGroup>
                                        <Label>반복 유형</Label>
                                        <Select value={recurringType} onChange={e => setRecurringType(e.target.value as RecurringType)}>
                                            <option value="INTERVAL">간격 반복 (분)</option>
                                            <option value="DAILY">매일</option><option value="WEEKLY">매주</option>
                                            <option value="MONTHLY">매월</option><option value="YEARLY">매년</option>
                                        </Select>
                                    </InputGroup>

                                    {recurringType === 'INTERVAL' ? (
                                        <InputGroup>
                                            <Label>반복 간격</Label>
                                            <Select value={intervalMinutes} onChange={e => setIntervalMinutes(Number(e.target.value))}>
                                                <option value="1">1분 마다</option><option value="5">5분 마다</option><option value="10">10분 마다</option>
                                            </Select>
                                        </InputGroup>
                                    ) : (
                                        <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', marginTop: '10px' }}>
                                            {recurringType === 'YEARLY' && (
                                                <InputGroup><Label>월</Label>
                                                    <Select value={selectedMonth} onChange={e => setSelectedMonth(Number(e.target.value))}>
                                                        {Array.from({length:12}, (_, i) => i + 1).map(m => <option key={m} value={m}>{m}월</option>)}
                                                    </Select>
                                                </InputGroup>
                                            )}
                                            {recurringType === 'WEEKLY' && (
                                                <InputGroup><Label>요일</Label>
                                                    <Select value={selectedDayOfWeek} onChange={e => setSelectedDayOfWeek(e.target.value)}>
                                                        <option value="MON">월요일</option><option value="TUE">화요일</option><option value="WED">수요일</option>
                                                        <option value="THU">목요일</option><option value="FRI">금요일</option><option value="SAT">토요일</option><option value="SUN">일요일</option>
                                                    </Select>
                                                </InputGroup>
                                            )}
                                            {(recurringType === 'MONTHLY' || recurringType === 'YEARLY') && (
                                                <InputGroup><Label>일</Label>
                                                    <Select value={selectedDayOfMonth} onChange={e => setSelectedDayOfMonth(Number(e.target.value))}>
                                                        {Array.from({length:31}, (_, i) => i + 1).map(d => <option key={d} value={d}>{d}일</option>)}
                                                    </Select>
                                                </InputGroup>
                                            )}
                                            <InputGroup><Label>시간</Label><Input type="time" value={selectedTime} onChange={e => setSelectedTime(e.target.value)} /></InputGroup>
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>

                        <ButtonGroup>
                            <ActionButton onClick={() => {setModalMode('NONE'); resetForm();}}>취소</ActionButton>
                            <ActionButton primary onClick={handleCreate}>{editingJobName ? '수정 내용 저장' : '스케줄 예약하기'}</ActionButton>
                        </ButtonGroup>
                    </ModalBox>
                </ModalOverlay>
            )}

            {modalMode === 'LIST' && (
                <ModalOverlay onClick={() => setModalMode('NONE')}>
                    <ModalBox onClick={e => e.stopPropagation()}>
                        <ModalTitle>예약된 알림 목록</ModalTitle>
                        <ListContainer>
                            {scheduleList.length === 0 ? (
                                <div style={{padding:'20px', textAlign:'center', color:'#888'}}>예약된 알림이 없습니다.</div>
                            ) : (
                                scheduleList.map((job, idx) => {
                                    let contentHtml = "내용 없음";
                                    let isDiscordNotify = false; // 디스코드 알림 여부 체크
                                    let imgPreviewUrl = "";

                                    const actionType = job.actionType || job.jobData?.actionType;
                                    const isChained = actionType === 'CHAINED_REST_API';

                                    try {
                                        if (isChained && job.jobData?.executionPlan) {
                                            // ✨ [해결 2] executionPlan이 문자열이므로 파싱 수행
                                            // 만약 이미 객체라면 JSON.parse 없이 사용하도록 방어 코드 작성
                                            const plan = typeof job.jobData.executionPlan === 'string'
                                                ? JSON.parse(job.jobData.executionPlan)
                                                : job.jobData.executionPlan;

                                            // 첫 번째 단계(마감 체크)에서 days 값 추출
                                            const step1 = plan.find((s: any) => s.order === 1);
                                            let daysVal = "?";

                                            if (step1 && step1.staticQueryParams) {
                                                // staticQueryParams도 문자열일 수 있으므로 파싱
                                                const qParams = typeof step1.staticQueryParams === 'string'
                                                    ? JSON.parse(step1.staticQueryParams)
                                                    : step1.staticQueryParams;
                                                daysVal = qParams.days || "?";
                                            }

                                            contentHtml = `🎯 <b>[워크플로우] 업무 마감 자동 알림</b><br/>(수집 기준: 마감 <b>${daysVal}일 전</b> 업무)`;
                                            isDiscordNotify = true;
                                        }
                                        else if (job.jobData?.message) {
                                            // 브라우저 알림 처리
                                            const parsed = typeof job.jobData.message === 'string' ? JSON.parse(job.jobData.message) : job.jobData.message;
                                            contentHtml = parsed.msg || contentHtml;
                                        }
                                        else if (job.jobData?.bodyParams || job.jobData?.queryParams) {
                                            // 디스코드 알림 처리 (params 또는 Params 확인)
                                            isDiscordNotify = true;
                                            const rawParams = job.jobData.bodyParams || job.jobData.queryParams;
                                            const parsed = typeof rawParams === 'string' ? JSON.parse(rawParams) : rawParams;

                                            const description = parsed.embeds?.[0]?.description || "";
                                            if (description.includes("**내용:**\n")) {
                                                contentHtml = description.split("**내용:**\n")[1];
                                            } else {
                                                contentHtml = description || parsed.content || contentHtml;
                                            }
                                            imgPreviewUrl = parsed.embeds?.[0]?.image?.url || "";
                                        }
                                    } catch(e) {
                                        console.error("데이터 파싱 중 오류 발생:", e);
                                        contentHtml = "데이터 구조 오류";
                                    }

                                    return (
                                        <ListItem key={idx}>
                                            <ItemInfo>
                                                {/* 🏷️ 타입 및 유형 뱃지 (가독성 강화) */}
                                                <div style={{ display: 'flex', gap: '6px', marginBottom: '6px' }}>
                                                    {/* ✨ 배경색과 텍스트의 조건을 isChained로 통일 */}
                                                    <SmallText style={{
                                                        background: isChained ? 'var(--primary-accent)' : '#5865F2',
                                                        color: 'white',
                                                        padding: '2px 6px',
                                                        borderRadius: '4px'
                                                    }}>
                                                        {isChained ? '복합 스케줄' : '단일 스케줄'}
                                                    </SmallText>

                                                    {/* 두 번째 뱃지도 동일한 변수를 사용하여 일관성 유지 */}
                                                    <SmallText style={{
                                                        background: isDiscordNotify ? '#5865F2' : '#ff4d4f',
                                                        color: 'white',
                                                        padding: '2px 6px',
                                                        borderRadius: '4px',
                                                        fontWeight: 'bold'
                                                    }}>
                                                        {isChained ? '마감 알림(Discord)' : (isDiscordNotify ? 'Discord' : 'Browser')}
                                                    </SmallText>
                                                    <SmallText style={{
                                                        background: 'var(--bg-input)', border: '1px solid var(--border-color)',
                                                        padding: '1px 6px', borderRadius: '4px', color: 'var(--text-normal)'
                                                    }}>
                                                        {job.jobType === 'ONCE' ? '단발성' : '반복성'}
                                                    </SmallText>
                                                </div>

                                                {job.description && (
                                                    <div style={{
                                                        fontSize: '0.9rem',
                                                        color: 'var(--primary-accent)',
                                                        fontWeight: 'bold',
                                                        marginBottom: '4px'
                                                    }}>
                                                        📝 {job.description}
                                                    </div>
                                                )}

                                                {/* 📝 내용 출력 */}
                                                <ListContentPreview onClick={handleImageClick}>
                                                    <div dangerouslySetInnerHTML={{ __html: contentHtml }} />
                                                    {imgPreviewUrl && <img src={imgPreviewUrl} alt="preview" />}
                                                </ListContentPreview>

                                                {/* ⏰ 주기 및 발송 시점 정보 (복구 완료!) */}
                                                <MiddleText style={{ marginTop: '6px', color: 'tan', fontWeight: '500' }}>
                                                    {job.jobType === 'RECURRING'
                                                        && `주기: ${parseCronToHumanReadable(job.cronExpression || "")}`}
                                                </MiddleText>
                                                <MiddleText style={{ marginTop: '6px', color: 'var(--primary-accent)', fontWeight: '500' }}>
                                                    다음 실행 시각: {job.nextFireTime?.replace('T', ' ')}
                                                </MiddleText>
                                            </ItemInfo>
                                            <div style={{ display: 'flex', gap: '6px' }}>
                                                <ActionButton onClick={() => openEditModal(job)} style={{fontSize: '0.75rem', padding: '4px 8px'}}>수정</ActionButton>
                                                <ActionButton onClick={() => openCopyModal(job)} style={{fontSize: '0.75rem', padding: '4px 8px'}}>복사</ActionButton>
                                                <ActionButton onClick={() => handleDelete(job.jobName, job.jobGroup)} style={{fontSize: '0.75rem', padding: '4px 8px'}}>삭제</ActionButton>
                                            </div>
                                        </ListItem>
                                    );
                                })
                            )}
                        </ListContainer>
                        <ButtonGroup><ActionButton onClick={() => setModalMode('NONE')}>닫기</ActionButton></ButtonGroup>
                    </ModalBox>
                    {/* ✨ [수정] 이미지 원본 보기 모달 (최상단 렌더링) */}
                    {selectedImgUrl && (
                        <ImageZoomModal
                            onClick={(e) => {
                                e.stopPropagation(); // ✨ 클릭 이벤트가 하위로 전달되는 것 방지
                                setSelectedImgUrl(null);
                            }}
                        >
                            <img
                                src={selectedImgUrl}
                                alt="Original"
                                onClick={e => e.stopPropagation()} // 이미지 자체 클릭 시에는 안 꺼짐
                            />
                            <div style={{ position: 'absolute', top: '20px', right: '20px', color: 'white', fontSize: '1.5rem', cursor: 'pointer' }}>✕</div>
                        </ImageZoomModal>
                    )}
                </ModalOverlay>
            )}
        </>
    );
};

export default SchedulerManager;